import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'How does AI for appliance repair help me get more jobs?',
    answer: 'Most appliance repair calls come in during the day when you\'re already on a job. Our AI answers every call immediately, qualifies the customer, captures their appliance type and issue, and books the appointment directly to your calendar — so you never lose a job to voicemail again.'
  },
  {
    question: 'Can the AI handle emergency repair calls after hours?',
    answer: 'Yes — that\'s one of the biggest wins for appliance repair businesses. When a customer\'s refrigerator dies at 9pm or their washer floods on a Saturday morning, your AI is there to answer, collect the details, and schedule the service call. You wake up to a full calendar instead of missed opportunities.'
  },
  {
    question: 'Will the AI know about different appliance brands and issues?',
    answer: 'Your AI is trained on your specific services, the brands you work on, and the types of repairs you offer. It can answer common questions about service areas, pricing ranges, and what to expect — and it captures the appliance make, model, and issue description so you show up prepared.'
  },
  {
    question: 'How does automated scheduling work for appliance repair?',
    answer: 'The AI checks your real-time calendar availability and books appointments directly — no back-and-forth, no double bookings. It sends confirmation texts and reminder messages to reduce no-shows, and you can set service windows by zip code or neighborhood to keep your routes efficient.'
  },
  {
    question: 'What about warranty and manufacturer service calls?',
    answer: 'Your AI can be configured to handle warranty inquiry calls specifically — collecting warranty information, model numbers, and purchase dates, then routing or scheduling appropriately. It keeps these calls organized and ensures you have everything you need before arriving on site.'
  },
  {
    question: 'How quickly can I get set up?',
    answer: 'Most appliance repair businesses are fully live within 48–72 hours. We handle the entire setup — training the AI on your services, connecting your calendar, and testing everything before launch. You don\'t need any technical knowledge.'
  },
];

const painPoints = [
  {
    icon: 'ri-phone-line',
    title: 'Missed Calls During Service Visits',
    description: 'You\'re elbow-deep in a dryer repair when three calls come in. Each one that goes to voicemail is a job that\'s probably going to your competitor who picked up.'
  },
  {
    icon: 'ri-moon-line',
    title: 'After-Hours Calls Going Unanswered',
    description: 'Appliances break at the worst times — evenings, weekends, holidays. Without 24/7 coverage, you\'re losing urgent jobs to whoever answers first.'
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Scheduling Conflicts & No-Shows',
    description: 'Manual scheduling leads to double bookings, forgotten appointments, and customers who don\'t show. Every no-show is wasted drive time and lost revenue.'
  },
  {
    icon: 'ri-time-line',
    title: 'Slow Follow-Up Losing Warm Leads',
    description: 'A customer submits a form or leaves a message. By the time you call back hours later, they\'ve already booked someone else. Speed wins in appliance repair.'
  },
];

const solutions = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Answering',
    description: 'Your AI answers every call in your company name, 24/7. It captures the appliance type, brand, issue description, and customer contact info — then books the appointment or routes the call appropriately. No more missed calls, no more lost jobs.',
    link: '/ai-phone-answering-service',
    linkLabel: 'Learn about AI Phone Answering'
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI Chatbot for Your Website',
    description: 'Capture leads from your website around the clock. The chatbot answers questions about your services, collects appliance details, and books appointments — even when you\'re on a job or asleep. Every website visitor becomes a potential booking.',
    link: '/services/ai-chatbot',
    linkLabel: 'Learn about AI Chatbot'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Scheduling',
    description: 'Customers book directly into your calendar based on your real availability. The system sends confirmation texts, appointment reminders, and follow-up messages automatically — reducing no-shows and keeping your schedule full and organized.',
    link: '/ai-appointment-scheduling',
    linkLabel: 'Learn about Automated Scheduling'
  },
  {
    icon: 'ri-send-plane-line',
    title: 'Automated Follow-Up',
    description: 'Every new lead gets a personalized response within 60 seconds via text and email. Post-service follow-ups go out automatically to collect reviews and re-engage customers for future repairs. Your pipeline stays warm without any manual effort.',
    link: '/services/ai-lead-follow-up',
    linkLabel: 'Learn about Automated Follow-Up'
  },
];

const benefits = [
  {
    icon: 'ri-calendar-2-line',
    stat: '40%',
    title: 'More Repair Jobs Booked',
    description: 'Capture every call and inquiry — including after-hours and weekend calls — and convert them into confirmed appointments automatically.'
  },
  {
    icon: 'ri-user-add-line',
    stat: '100%',
    title: 'Lead Capture Rate',
    description: 'No more leads slipping through the cracks. Every call, form submission, and chat inquiry is captured, responded to, and followed up with.'
  },
  {
    icon: 'ri-time-line',
    stat: '14 hrs',
    title: 'Admin Time Saved Per Week',
    description: 'Stop spending hours on scheduling, follow-ups, and phone tag. AI handles the repetitive work so you can focus on repairs and growing your business.'
  },
  {
    icon: 'ri-moon-line',
    stat: '24/7',
    title: 'Always-On Availability',
    description: 'Your AI never sleeps, never takes a day off, and never misses a call. Customers get immediate responses at any hour — and you get more booked jobs.'
  },
];

const useCases = [
  {
    icon: 'ri-alarm-warning-line',
    title: 'Emergency Appliance Repairs',
    description: 'A refrigerator full of food stops working on a Friday night. Your AI answers immediately, captures the urgency and appliance details, and gets the customer scheduled for your earliest available slot — often the same evening or next morning. You capture the emergency job instead of losing it to whoever answers first.',
    tag: 'High urgency'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Warranty & Manufacturer Service Calls',
    description: 'Warranty calls require specific information — model numbers, purchase dates, warranty documentation. Your AI is trained to collect all of this upfront, so when you arrive on site you have everything you need. No more showing up unprepared or making a second trip for missing info.',
    tag: 'Organized intake'
  },
  {
    icon: 'ri-calendar-schedule-line',
    title: 'Routine Service Scheduling',
    description: 'Customers calling to schedule a washer tune-up, dryer vent cleaning, or dishwasher inspection get booked instantly without any back-and-forth. The AI checks your availability, books the slot, and sends a confirmation — all in under two minutes. Your calendar fills itself.',
    tag: 'Fully automated'
  },
];

const steps = [
  {
    number: '01',
    title: 'We Set Up Your AI',
    description: 'We train your AI on your services, service area, brands you work on, and pricing. Setup takes 48–72 hours and we handle everything — no technical work required from you.'
  },
  {
    number: '02',
    title: 'AI Answers & Books',
    description: 'Your AI starts answering calls, responding to website inquiries, and booking appointments directly to your calendar. Every lead is captured and followed up with automatically.'
  },
  {
    number: '03',
    title: 'You Show Up & Do the Work',
    description: 'You arrive to confirmed appointments with full customer and appliance details already collected. No more phone tag, no more scheduling headaches — just more repair jobs.'
  },
];

export default function AIForApplianceRepair() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI for Appliance Repair Businesses | 24/7 Call Answering & Scheduling | Dino Nuggets"
        description="Stop missing appliance repair calls. AI for appliance repair businesses answers every call 24/7, books service appointments automatically, and follows up with every lead. Get more repair jobs without more admin work."
        canonical="/industries/ai-for-appliance-repair"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Appliance Repair Businesses',
              description: 'AI-powered call answering, automated scheduling, and lead follow-up for appliance repair companies. Capture every call, book more repair jobs, and provide 24/7 availability.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
              keywords: 'AI for appliance repair, appliance repair automation, AI phone answering appliance repair, automated scheduling appliance repair',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Spring Sale Banner */}
        <div className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-white rounded-full translate-x-1/4 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white rounded-full translate-y-1/2"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <span className="text-lg">🌱</span>
              <span className="font-bold text-base tracking-wide uppercase">Spring Sale</span>
              <span className="hidden sm:block w-px h-5 bg-white/40"></span>
            </div>
            <p className="text-sm sm:text-base font-medium text-white/95">
              <strong className="font-bold text-white">No setup fees</strong> on all automation packages through April 30th — save up to $500 on getting started.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="flex-shrink-0 bg-white text-emerald-600 hover:bg-emerald-50 px-4 py-1.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Offer
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/appliance-repair-hero-001.jpg"
              alt="AI Automation for Appliance Repair Businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <span className="inline-block bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Built for Appliance Repair Companies
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              AI Automation for Appliance Repair Businesses
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              Stop missing service calls and book more repair jobs with AI-powered automation. Answer every call 24/7, schedule appointments automatically, and follow up with every lead — without lifting a finger.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Book a Free Consultation
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '24/7', label: 'Call answering — never miss a repair job again' },
              { value: '60 sec', label: 'Average AI response time to new leads' },
              { value: '40%', label: 'More appointments booked on average' },
              { value: '48 hrs', label: 'From sign-up to fully automated' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* Pain Points */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Why Appliance Repair Businesses Lose Jobs Every Day
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                As part of our broader <Link to="/industries/ai-for-home-services" className="text-primary hover:underline font-medium">AI for home services</Link> platform, AI for appliance repair solves the four biggest operational problems that cost repair businesses revenue every single week:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {painPoints.map((point) => (
                  <div key={point.title} className="bg-gray-50 p-6 rounded-xl flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg">
                      <i className={`${point.icon} text-2xl text-red-400`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{point.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* The Real Cost */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                The Real Cost of Missed Appliance Repair Calls
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Appliance repair is a high-urgency, high-competition business. When a customer's refrigerator breaks down or their washer stops mid-cycle, they call the first company that answers. If that's not you, it's your competitor. Our <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> ensures you're always first to respond — no matter when the call comes in.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { stat: '$250–$600', color: 'text-red-400', border: 'border-red-100', label: 'Average appliance repair job value', note: 'Each missed call is potentially hundreds of dollars walking out the door.' },
                  { stat: '78%', color: 'text-amber-400', border: 'border-amber-100', label: 'Customers hire the first business that responds', note: 'Speed of response is the #1 factor in winning appliance repair jobs.' },
                  { stat: '24/7', color: 'text-primary', border: 'border-primary/20', label: 'When appliances break down', note: 'Evenings, weekends, and holidays are peak emergency call times for repair businesses.' },
                ].map((item) => (
                  <div key={item.stat} className="bg-white p-6 rounded-xl border" style={{ borderColor: undefined }}>
                    <div className={`text-3xl font-bold ${item.color} mb-2`}>{item.stat}</div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">{item.label}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{item.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Solution */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How Dino Nuggets AI Works for Appliance Repair
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Our complete AI automation stack is built specifically for service businesses like yours. Here's what gets automated when you use AI for appliance repair:
              </p>
              <div className="space-y-6">
                {solutions.map((solution) => (
                  <div key={solution.title} className="bg-gray-50 p-6 rounded-xl flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <i className={`${solution.icon} text-2xl text-primary`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-xl text-gray-900 mb-2">{solution.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">{solution.description}</p>
                      <Link
                        to={solution.link}
                        className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1 cursor-pointer"
                      >
                        {solution.linkLabel}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                What You Get With AI for Appliance Repair
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Real, measurable results that show up in your calendar and your revenue:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                        <i className={`${benefit.icon} text-2xl text-primary`}></i>
                      </div>
                      <div className="text-3xl font-bold text-primary">{benefit.stat}</div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How It Works: 3 Simple Steps
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Getting started with AI for appliance repair is easier than you think. No technical knowledge required.
              </p>
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl">
                      <span className="text-primary font-bold text-lg">{step.number}</span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="hidden lg:block absolute left-7 mt-14 w-0.5 h-6 bg-primary/20"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Use Cases */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Real-World Use Cases for Appliance Repair AI
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Here's exactly how AI for appliance repair handles the calls and situations your business deals with every day:
              </p>
              <div className="space-y-6">
                {useCases.map((useCase) => (
                  <div key={useCase.title} className="border border-gray-200 rounded-xl p-6 hover:border-primary/40 transition-colors">
                    <div className="flex items-start gap-5">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                        <i className={`${useCase.icon} text-2xl text-primary`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-semibold text-xl text-gray-900">{useCase.title}</h3>
                          <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">{useCase.tag}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{useCase.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Links / Related Services */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Core Services Powering Your Appliance Repair Automation
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our AI for appliance repair is built on two core services — explore each one in detail:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Link
                  to="/ai-phone-answering-service"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Phone Answering Service</h3>
                    <p className="text-sm text-gray-600">Professional AI that answers every call in your company name, 24/7 — capturing leads and booking jobs automatically.</p>
                  </div>
                </Link>
                <Link
                  to="/ai-appointment-scheduling"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Appointment Scheduling</h3>
                    <p className="text-sm text-gray-600">Automatic calendar booking for service calls — with confirmations, reminders, and follow-ups sent to every customer.</p>
                  </div>
                </Link>
                <Link
                  to="/services/ai-chatbot"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-smile-3-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Chatbot</h3>
                    <p className="text-sm text-gray-600">Capture and qualify website leads 24/7 — answer questions, collect appliance details, and book appointments automatically.</p>
                  </div>
                </Link>
                <Link
                  to="/services/ai-lead-follow-up"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-send-plane-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Lead Follow-Up</h3>
                    <p className="text-sm text-gray-600">Respond to every new lead in 60 seconds and nurture them to a booked appointment — automatically.</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Other Industries */}
            <div className="mb-20">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                See How We Help Other Home Service Businesses
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/ai-for-contractors" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Contractors</Link>
                <Link to="/ai-for-roofers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Roofers</Link>
                <Link to="/ai-for-landscapers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Landscapers</Link>
                <Link to="/industries/ai-for-home-services" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Home Services</Link>
                <Link to="/ai-for-rv-parks" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for RV Parks &amp; Campgrounds</Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Never Miss Another Repair Job
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Every unanswered call is a job going to your competitor. Our AI for appliance repair answers every call, books every appointment, and follows up with every lead — 24/7, automatically. Get set up in 48 hours.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Get Started Today
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />
        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
